import { Link } from '@yoast/ui-library';
import React from 'react';

import type { Scan } from '../../api/generated/insights.schemas';
import {
  formatNextRetryDate,
  getEarliestNextRetryAfter,
  getNotificationType,
} from './scanRetryUtils';
import useManualRetryScanWithInvalidation from './useManualRetryScanWithInvalidation';

interface IncompleteAnalysisMessageProps {
  failedScans: Scan[];
  brandId: string;
  analysisId: string;
}

const IncompleteAnalysisMessage: React.FC<IncompleteAnalysisMessageProps> = ({
  failedScans,
  brandId,
  analysisId,
}) => {
  const notificationType = getNotificationType(failedScans);

  const { mutate: retryScan, isPending: isRetrying } = useManualRetryScanWithInvalidation(
    brandId,
    analysisId,
  );

  if (notificationType === 'none') {
    return null;
  }

  const handleRetry = (event: React.MouseEvent) => {
    event.preventDefault();

    failedScans
      .filter((scan) => scan.canManualRetry)
      .forEach((scan) => {
        retryScan({ brandId, analysisId, scanId: scan.id });
      });
  };

  if (notificationType === 'can_retry') {
    if (isRetrying) {
      return (
        <p className="yst-text-sm yst-text-red-600 yst-mt-1 yst-mb-0">
          Preparing the scan retry...
        </p>
      );
    }

    return (
      <p className="yst-text-sm yst-text-red-600 yst-mt-1 yst-mb-0">
        Analysis incomplete. Please{' '}
        <Link
          as="button"
          type="button"
          className="yst-text-sm yst-text-red-600"
          onClick={handleRetry}
          variant="default"
        >
          run analysis again
        </Link>
        .
      </p>
    );
  }

  if (notificationType === 'cooldown') {
    const nextRetryAfter = getEarliestNextRetryAfter(failedScans);

    return (
      <p className="yst-text-sm yst-text-red-600 yst-mt-1 yst-mb-0">
        Analysis incomplete.{' '}
        {nextRetryAfter
          ? `You can run the analysis again ${formatNextRetryDate(nextRetryAfter)}.`
          : 'You can run the analysis again later.'}
      </p>
    );
  }

  return (
    <p className="yst-text-sm yst-text-red-600 yst-mt-1 yst-mb-0">
      Analysis incomplete. Please{' '}
      <Link
        href="mailto:support@yoast.com"
        className="yst-text-sm yst-text-red-600"
        variant="default"
      >
        contact support
      </Link>
      .
    </p>
  );
};

export default IncompleteAnalysisMessage;
